// The single network path for the buyer SPA. Every page goes through here, so
// this is also the one place that can recognise "your session is gone" and hand
// the app to the OTP gate instead of letting each caller toast a generic error.

const buildQueryString = (params) => {
  if (!params) return '';
  return `?${new URLSearchParams(params).toString()}`;
};

// Carries the pieces of the server's error envelope that a caller may need to
// branch on. Subclassing Error (rather than returning a plain object) keeps
// `err.message` working, so the existing `catch (err) { showToast(err.message) }`
// call sites are unaffected.
export class ApiError extends Error {
  constructor({
    message, status, code, errorDetails,
  }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errorDetails = errorDetails || {};
  }
}

// A 401 is the only status that means "re-authenticate"; everything else is an
// ordinary failure the page should render itself.
export const isAuthError = (err) => err instanceof ApiError && err.status === 401;

// Registered by the buyer-auth provider on mount. Module-level rather than
// passed per call because any request on any page can be the one that discovers
// the session has expired.
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = typeof fn === 'function' ? fn : null;
};

// An error page from the load balancer is HTML, and response.json() throws a
// SyntaxError on it — which would mask the status entirely and surface a 502 as
// an unreadable parse error. Fall back to an empty body and let the status speak.
const parseBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

// Where a request goes. Deployed builds set REACT_APP_HOST_API_URL to "/", so the
// checkout talks to whichever host served it rather than to one host baked in at
// build time. That is what lets the buyer session work at all: the production
// bundle serves every org, NextIAS buyers land on ordergate.nextias.com, and a
// hard-coded ordergate.madeeasy.in made every call from their page cross-site -
// the session cookie from a successful OTP or autologin was dropped on arrival.
//
// Joined rather than concatenated, because a "/" base meeting a path that starts
// with "/" gives "//view/order/..." - which a browser reads as a protocol-relative
// URL to a host called "view". Nothing passes a leading slash today; this makes
// sure nothing ever can. A missing value falls back to the serving host too,
// since that is the only answer that is right in every deployed environment.
export const apiUrl = (url) => {
  const base = process.env.REACT_APP_HOST_API_URL || '/';
  return `${base.replace(/\/+$/, '')}/${String(url).replace(/^\/+/, '')}`;
};

const fetchAPI = async (
  url,
  method = 'GET',
  data = null,
  params = null,
  headers = {},
  { skipAuthHandler = false } = {},
) => {
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...(data && { body: JSON.stringify(data) }),
  };

  const fullUrl = `${apiUrl(url)}${buildQueryString(params)}`;
  const response = await fetch(fullUrl, options);
  const responseJson = await parseBody(response);

  if (!response.ok) {
    const error = new ApiError({
      message: responseJson?.message || 'API Error',
      status: response.status,
      code: responseJson?.code,
      errorDetails: responseJson?.errorDetails,
    });

    // The gate takes over the page. Skipped by the auth endpoints themselves —
    // verify-otp answers 401 on a wrong code, which must stay a field error
    // rather than tearing down the screen the user is typing into.
    if (response.status === 401 && !skipAuthHandler && unauthorizedHandler) {
      unauthorizedHandler(error);
    }

    throw error;
  }

  return responseJson?.data;
};

export const apiService = {
  get: (url, params, headers, options) => fetchAPI(url, 'GET', null, params, headers, options),
  post: (url, data, headers, options) => fetchAPI(url, 'POST', data, null, headers, options),
  put: (url, data, headers, options) => fetchAPI(url, 'PUT', data, null, headers, options),
  delete: (url, headers, options) => fetchAPI(url, 'DELETE', null, null, headers, options),
};

export default apiService;
